import { useEffect, useState } from 'react'
import { MdDelete } from 'react-icons/md'

import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'

import Button from '~/components/Buttons/Button'
import Panel from '~/components/Panel'
import SortableCell from '~/components/Sortable/Cell'
import SortableRow from '~/components/Sortable/Row'
import Table from '~/components/Table'
import { Select } from '~/components/unform'
import useFetchSWR from '~/hooks/useFetchSWR'

const RedirectLocations = ({
  formRef,
  initialData,
  redirectLocations,
  setRedirectLocations
}) => {
  const { data: locationsData, error: locationsError } = useFetchSWR({
    url: `/api/location/minified`,
    revalidateOnFocus: false
  })

  const [locationOptions, setLocationOptions] = useState([])

  useEffect(() => {
    if (locationsData) {
      const options = locationsData.data
        .filter(location => location.active)
        .map(location => ({
          id: location.id,
          value: location.id,
          text: location.name
        }))
      setLocationOptions(options)
    }
  }, [locationsData, locationsError])

  useEffect(() => {
    if (initialData?.redirect_locations?.length && locationOptions.length > 0) {
      const preSelected = initialData?.redirect_locations
        .map(item => {
          const found = locationOptions.find(opt => opt.id === item.location_id)
          return found ? { ...found, priority: item.priority } : null
        })
        .filter(Boolean)
        .sort((a, b) => a.priority - b.priority)

      setRedirectLocations(preSelected)
    }
  }, [initialData, locationOptions, setRedirectLocations])

  const handleSelectStore = selectedId => {
    const selected = locationOptions.find(option => option.id === selectedId)
    if (!selected) return

    const alreadySelected = redirectLocations.find(loc => loc.id === selectedId)
    if (alreadySelected) return

    setRedirectLocations(prev => [...prev, selected])
  }

  const handleDeleteStore = id => {
    setRedirectLocations(prev => prev.filter(loc => loc.id !== id))
  }

  const handleDragEnd = event => {
    const { active, over } = event
    if (active.id !== over?.id) {
      const oldIndex = redirectLocations.findIndex(loc => loc.id === active.id)
      const newIndex = redirectLocations.findIndex(loc => loc.id === over?.id)

      setRedirectLocations(prev => arrayMove(prev, oldIndex, newIndex))
    }
  }

  useEffect(() => {
    const formattedLocations = redirectLocations.map((loc, index) => ({
      location_id: loc.id,
      priority: index + 1
    }))
    formRef.current.setFieldValue('redirect_locations', formattedLocations)
  }, [formRef, redirectLocations])

  const schema = {
    name: {
      title: 'Lojas Selecionadas',
      render: rowData => (
        <div style={{ width: '100%' }}>
          <SortableCell id={rowData.id} text={rowData.text} />
        </div>
      ),
      style: { width: '80%' }
    },
    actions: {
      title: '',
      render: rowData => (
        <div style={{ textAlign: 'right' }}>
          <Button
            type="button"
            className="no-drag"
            onClick={e => {
              e.stopPropagation()
              handleDeleteStore(rowData.id)
            }}
            iconRight={<MdDelete />}
            size="small"
            template="danger"
          >
            Remover
          </Button>
        </div>
      ),
      style: { width: '20%', textAlign: 'right' }
    }
  }

  return (
    <Panel
      title="Redirecionamento de entrega"
      description="Defina abaixo quais lojas podem receber o redirecionamento e a sua ordem de prioridade."
    >
      <Select
        name="redirect_locations_select"
        placeholder="Selecione uma loja"
        options={locationOptions}
        onChange={handleSelectStore}
        className="selectWrapper"
      />
      <hr />
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={redirectLocations?.map(loc => loc.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            data={redirectLocations}
            schema={schema}
            rowWrapper={row => (
              <SortableRow key={row.id} id={row.id}>
                {row.children}
              </SortableRow>
            )}
          />
        </SortableContext>
      </DndContext>
    </Panel>
  )
}

export default RedirectLocations
